import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../../data/mock';
import { LogoUpim } from '../ui-upim';
import {
  Facebook,
  Instagram,
  Twitter,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink
} from 'lucide-react';

const columnas = [
  {
    titulo: 'Institución',
    enlaces: [
      { name: 'Inicio', to: '/' },
      { name: '¿Quiénes Somos?', to: '/nosotros' },
      { name: 'Beneficios', to: '/beneficios' },
      { name: 'Afíliate', to: '/afiliacion' },
      { name: 'Calendario', to: '/calendario' },
      { name: 'Recursos', to: '/recursos' },
    ],
  },
  {
    titulo: 'Servicios',
    enlaces: [
      { name: 'Proveedores Certificados', to: '/proveedores' },
      { name: 'Academia UPIM', to: '/academia' },
      { name: 'Avalúos con IA', to: '/beneficios' },
      { name: 'Bolsa Inmobiliaria (BIC)', href: 'https://upim.easybroker.com/' },
    ],
  },
];

const redes = [
  { key: 'instagram', icon: Instagram, label: 'Instagram' },
  { key: 'twitter', icon: Twitter, label: 'Twitter' },
  { key: 'facebook', icon: Facebook, label: 'Facebook' },
  { key: 'whatsapp', icon: MessageCircle, label: 'WhatsApp' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative border-t border-[var(--upim-borde)] bg-black" data-testid="footer-upim">
      <div className="upim-halo pointer-events-none absolute inset-x-0 top-0 h-52" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <LogoUpim className="h-12" claseTexto="text-2xl" testId="footer-logo" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[var(--upim-texto-2)]">
              Unión de Profesionales Inmobiliarios de México A.C. Formación, blindaje jurídico,
              red nacional y una plataforma con CRM e inteligencia artificial para el socio UPIM.
            </p>
            <div className="mt-6 flex gap-3">
              {redes.map(({ key, icon: Icono, label }) => (
                <a
                  key={key}
                  href={companyInfo.social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-red-${key}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--upim-borde)] text-[var(--upim-texto-2)] transition-[color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--upim-acento)] hover:text-[var(--upim-acento)]"
                >
                  <Icono className="h-4.5 w-4.5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {columnas.map((col) => (
            <div key={col.titulo} className="lg:col-span-2">
              <h4 className="mb-5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--upim-acento)]">
                {col.titulo}
              </h4>
              <ul className="space-y-3">
                {col.enlaces.map((e) => (
                  <li key={e.name}>
                    {e.href ? (
                      <a
                        href={e.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-texto)]"
                      >
                        {e.name}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    ) : (
                      <Link
                        to={e.to}
                        className="text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-texto)]"
                      >
                        {e.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-4">
            <h4 className="mb-5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--upim-acento)]">
              Contacto
            </h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 flex-shrink-0 text-[var(--upim-texto-3)]" aria-hidden="true" />
                <a href={`mailto:${companyInfo.email}`} className="text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-texto)]">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 text-[var(--upim-texto-3)]" aria-hidden="true" />
                <a href={`tel:${companyInfo.phone}`} className="text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-texto)]">
                  55 7116 4745
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="h-4 w-4 flex-shrink-0 text-[var(--upim-texto-3)]" aria-hidden="true" />
                <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-texto)]">
                  WhatsApp: 55 7116 4745
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="upim-linea" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 lg:px-12">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-xs text-[var(--upim-texto-3)]">
            © {currentYear} {companyInfo.name}. Todos los derechos reservados.
          </p>
          <div className="flex gap-6">
            <Link to="/aviso-privacidad" className="text-xs text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-texto)]">
              Aviso de Privacidad
            </Link>
            <Link to="/terminos" className="text-xs text-[var(--upim-texto-3)] transition-colors hover:text-[var(--upim-texto)]">
              Términos y Condiciones
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
